import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { money } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MES_LARGO = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

export default function MonthlySales({ sym }) {
  const nav = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/monthly", { params: { year } }).then((r) => setData(r.data));
  }, [year]);

  if (!data) return null;
  const chart = data.months.map((m) => ({ name: MES[m.month - 1], total: m.total }));
  const open = (m) => nav(`/reportes?mes=${year}-${String(m).padStart(2, "0")}`);

  return (
    <section className="rounded-xl border bg-white mb-8 fade-up" data-testid="monthly-sales">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b">
        <div>
          <h3 className="font-bold">Ventas por mes</h3>
          <p className="text-xs text-stone-500">Total del año: <span className="font-mono font-semibold" data-testid="monthly-year-total">{money(data.totals.total, sym)}</span> · {data.totals.count} notas</p>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-28" data-testid="monthly-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{data.years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid lg:grid-cols-[1.2fr_1fr]">
        <div className="h-72 p-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={70} tickFormatter={(v) => `${sym}${Number(v).toLocaleString("es-MX")}`} />
              <Tooltip formatter={(v) => money(v, sym)} labelStyle={{ fontWeight: 700 }} />
              <Bar dataKey="total" name="Ventas" fill="#B45309" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(_, i) => open(i + 1)} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="overflow-x-auto border-t lg:border-t-0 lg:border-l">
          <table className="w-full text-xs">
            <thead className="bg-stone-50 text-left uppercase tracking-wider text-stone-500">
              <tr><th className="px-3 py-2">Mes</th><th className="px-3 py-2 text-right">Notas</th><th className="px-3 py-2 text-right">Vendido</th><th className="px-3 py-2 text-right">Cobrado</th><th className="px-3 py-2 text-right">Por cobrar</th></tr>
            </thead>
            <tbody className="divide-y">
              {data.months.map((m) => (
                <tr key={m.month} onClick={() => open(m.month)} className={`cursor-pointer hover:bg-amber-50/60 ${m.count ? "" : "text-stone-400"}`} data-testid={`monthly-row-${m.month}`}>
                  <td className="px-3 py-1.5 font-semibold">{MES_LARGO[m.month - 1]}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{m.count}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{money(m.total, sym)}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{money(m.paid, sym)}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{money(m.pending, sym)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
